import { useEffect, useMemo, useState, useRef } from 'react';
import { Box, Typography, CircularProgress, Alert, IconButton, Chip, Dialog, DialogTitle, DialogContent, DialogActions, Button, Switch, FormControlLabel } from '@mui/material';
import { setChamberReadiness } from '../../services/api';
import { KeyboardArrowLeft, KeyboardArrowRight, Visibility, VisibilityOff } from '@mui/icons-material';
import PageLayout from '../../components/layout/frames/PageLayout';
import HubHealthStatus from '../../components/hub/HubHealthStatus';

import { useHubHealthStore } from '../../store/hubHealthStore';
import { useLogsStore } from '../../store/logsStore';
import { usePreferencesStore } from '../../store/preferencesStore';
import { useRaspiStore } from '../../store/raspiStore';
import { useCameraStore } from '../../store/cameraStore';
import { useChamberStore } from '../../store/chamberStore';
import RaspiStatusIndicator from '../../components/raspi/RaspiStatusIndicator';
import RaspiMenu from '../../components/raspi/RaspiMenu';
import { useAuthStore } from '../../store/authStore';
import { useUnauthorizedAction } from '../../App';
import { Table, TableCell, TableRow, TableHead, TableBody, TableContainer, Paper} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import LoadRaspiDataDialog from '../../components/dialogs/LoadRaspiDataDialog';
import CleanRaspiFilesDialog from '../../components/dialogs/CleanRaspiFilesDialog';
import UpdateRaspiSoftwareDialog from '../../components/dialogs/UpdateRaspiSoftwareDialog';
import { useExperimentStore } from '../../store/experimentStore';
import type { Experiment } from '../../types/experiment';

const Dashboard = () => {
  const { startPeriodicCheck, stopPeriodicCheck } = useHubHealthStore();
  const { 
    sysLogs: logEntries, 
    isLoading: logLoading, 
    fetchLogsPage 
  } = useLogsStore();
  
  // Local state for error handling
  const [logError, setLogError] = useState<string | null>(null);
  const { 
    showHubLog: showLogFrame, 
    logFrameExpanded: isLogFrameExpanded,
    updatePreference 
  } = usePreferencesStore();

  const { allRaspis, fetchAllRaspis } = useRaspiStore();
  const { allCameras, fetchAllCameras } = useCameraStore();
  const { allChambers, fetchAllChambers } = useChamberStore();
  const { allExperiments, fetchAllExperiments } = useExperimentStore();
  
  // Create stable references for fetch functions to prevent infinite re-renders
  const fetchAllRaspisRef = useRef(fetchAllRaspis);
  const fetchAllCamerasRef = useRef(fetchAllCameras);
  const fetchAllChambersRef = useRef(fetchAllChambers);
  const fetchAllExperimentsRef = useRef(fetchAllExperiments);
  
  // Create stable references for health check functions
  const startPeriodicCheckRef = useRef(startPeriodicCheck);
  const stopPeriodicCheckRef = useRef(stopPeriodicCheck);
  
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [dataFetched, setDataFetched] = useState(false);
  
  // Count Raspis by status
  const raspiList = Object.values(allRaspis);
  const availableCount = raspiList.filter(r => r.raspi_status === 'AVAILABLE').length;
  const inUseCount = raspiList.filter(r => r.raspi_status === 'IN_USE').length;
  const disconnectedCount = raspiList.filter(r => r.raspi_status === 'DISCONNECTED').length;
  // Filtered Raspis
  const filteredRaspis = statusFilter ? raspiList.filter(r => r.raspi_status === statusFilter) : raspiList;

  // Dialog state for menu actions
  const [loadRaspiDataDialogOpen, setLoadRaspiDataDialogOpen] = useState(false);
  const [cleanRaspiFilesDialogOpen, setCleanRaspiFilesDialogOpen] = useState(false);
  const [updateRaspiSoftwareDialogOpen, setUpdateRaspiSoftwareDialogOpen] = useState(false);
  const [rebootRaspiDialogOpen, setRebootRaspiDialogOpen] = useState(false);
  const [restartRaspiDialogOpen, setRestartRaspiDialogOpen] = useState(false);
  const [activeRaspiId, setActiveRaspiId] = useState<string | null>(null);

  // --- Ready toggle: optimistic state + colour indicators ---
  // The Ready switch used to bind straight to chamber_status, which only
  // updates after a full round-trip (click -> hub -> Pi -> Arduino -> MQTT
  // back), so it snapped back on click and looked dead. We track the desired
  // position per chamber so the switch moves instantly, then reconcile with the
  // real status when it arrives (or revert after a timeout if it never does).
  const [pendingReady, setPendingReady] = useState<Record<string, boolean>>({});

  useEffect(() => {
    setPendingReady((prev) => {
      const entries = Object.entries(prev);
      if (entries.length === 0) return prev;
      let changed = false;
      const next = { ...prev };
      for (const [cid, desired] of entries) {
        const ch: any = (allChambers as any)[cid];
        if (ch && (ch.chamber_status === 'READINESS') === desired) {
          delete next[cid];
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [allChambers]);

  const handleReadyToggle = async (raspiId: string, chamberId: string, desired: boolean) => {
    setPendingReady((prev) => ({ ...prev, [chamberId]: desired }));
    // Fallback: if the command never takes effect (e.g. chamber briefly
    // disconnected), drop the optimistic state after 20s so the switch reverts
    // to reality rather than lying.
    window.setTimeout(() => {
      setPendingReady((prev) => {
        if (!(chamberId in prev)) return prev;
        const n = { ...prev }; delete n[chamberId]; return n;
      });
    }, 20000);
    try {
      await setChamberReadiness(raspiId, desired);
    } catch {
      setPendingReady((prev) => { const n = { ...prev }; delete n[chamberId]; return n; });
    }
  };

  // Direction-aware Ready colour vs the 35 C readiness setpoint:
  // off = grey, warming (below by >0.5) = yellow, within +/-0.5 = green,
  // overshoot (above by >0.5) = red.
  const READINESS_TARGET_C = 35;
  const readyVisual = (isOn: boolean, t: number | null | undefined) => {
    if (!isOn) return { dot: '#9e9e9e', sw: 'default' as const, txt: 'text.secondary' as const };
    if (t == null) return { dot: '#fbc02d', sw: 'warning' as const, txt: '#f9a825' };
    const d = t - READINESS_TARGET_C;
    if (Math.abs(d) <= 0.5) return { dot: '#43a047', sw: 'success' as const, txt: '#2e7d32' };
    if (d < 0) return { dot: '#fbc02d', sw: 'warning' as const, txt: '#f9a825' };
    return { dot: '#e53935', sw: 'error' as const, txt: '#c62828' };
  };

  // Handlers for menu actions
  const handleLoadRaspiData = (raspiId: string) => {
    setActiveRaspiId(raspiId);
    setLoadRaspiDataDialogOpen(true);
  };
  const handleCleanRaspiFiles = (raspiId: string) => {
    setActiveRaspiId(raspiId);
    setCleanRaspiFilesDialogOpen(true);
  };
  const handleUpdateRaspiSoftware = (raspiId: string) => {
    setActiveRaspiId(raspiId);
    setUpdateRaspiSoftwareDialogOpen(true);
  };
  const handleRebootRaspi = (raspiId: string) => {
    setActiveRaspiId(raspiId);
    setRebootRaspiDialogOpen(true);
  };
  const handleRestartRaspiSoftware = (raspiId: string) => {
    setActiveRaspiId(raspiId);
    setRestartRaspiDialogOpen(true);
  };

  // Confirm handlers (API calls)
  const handleConfirmReboot = async () => {
    setRebootRaspiDialogOpen(false);
    if (!activeRaspiId) return;
    try {
      // Call your API here, e.g. await rebootRaspi(activeRaspiId);
    } catch (error) {
      // Handle error
    }
  };
  const handleConfirmRestart = async () => {
    setRestartRaspiDialogOpen(false);
    if (!activeRaspiId) return;
    try {
      // Call your API here, e.g. await restartRaspiSoftware(activeRaspiId);
    } catch (error) {
      // Handle error
    }
  };

  const navigate = useNavigate();

  useEffect(() => {
    // Start periodic health checking when component mounts
    startPeriodicCheckRef.current();

    // Cleanup when component unmounts
    return () => {
      stopPeriodicCheckRef.current();
    };
  }, []);

  // Create stable reference for fetchLogsPage
  const fetchLogsPageRef = useRef(fetchLogsPage);
  
  useEffect(() => {
    const initializeLogs = async () => {
      try {
        // Fetch initial logs
        await fetchLogsPageRef.current(1);
        // WebSocket connection is handled by App.tsx - no need to connect here
        setLogError(null);
      } catch (error) {
        console.error('Error initializing log streaming:', error);
        setLogError('Failed to initialize logs');
      }
    };
    initializeLogs();
  }, []);

  // Ensure data is fetched on mount - preferences are now hardcoded
  const preferencesLoaded = true; // Preferences are hardcoded, always loaded
  
  useEffect(() => {
    if (!preferencesLoaded || dataFetched) return; // Wait for preferences to be loaded and only fetch once
    
    if (Object.keys(allRaspis).length === 0) fetchAllRaspisRef.current();
    if (Object.keys(allCameras).length === 0) fetchAllCamerasRef.current();
    if (Object.keys(allChambers).length === 0) fetchAllChambersRef.current();
    if (Object.keys(allExperiments).length === 0) fetchAllExperimentsRef.current();
    
    setDataFetched(true);
  }, [preferencesLoaded, dataFetched, allRaspis, allCameras, allChambers, allExperiments]);

  // Helper function to get log level color
  const getLogLevelColor = (level: string | undefined) => {
    switch ((level || '').toUpperCase()) {
      case 'ERROR':
        return '#f44336'; // Red
      case 'WARNING':
      case 'WARN':
        return '#ff9800'; // Orange
      case 'INFO':
        return '#2196f3'; // Blue
      case 'DEBUG':
        return '#9e9e9e'; // Gray
      default:
        return '#000000'; // Black
    }
  };

  // Helper function to get source color
  const getSourceColor = (source: string | undefined) => {
    if (!source) return '#9e9e9e'; // Gray for undefined
    if (source.includes('SYS')) {
      return '#2196f3'; // Blue for system messages
    } else if (source.includes('RASPI')) {
      return '#4caf50'; // Green for raspi messages
    } else {
      return '#9e9e9e'; // Gray for others
    }
  };

  // Toggle log frame expansion (for arrow button inside log frame)
  const toggleLogFrameExpansion = () => {
    updatePreference('logFrameExpanded', !isLogFrameExpanded);
  };

  // Toggle log frame visibility (for hide/show button in top bar) - now using store
  const toggleLogFrameVisibility = () => {
    updatePreference('showHubLog', !showLogFrame);
  };

  const getRole = useAuthStore((state) => state.getRole);
  const isViewer = getRole() === 'viewer' || getRole() === 'public';
  const triggerUnauthorized = useUnauthorizedAction();

  // Upper frame content with status counters
  const upFrameContent = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexGrow: 1, justifyContent: 'space-between' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Chip
          label={`AVAILABLE: ${availableCount}`}
          color={statusFilter === 'AVAILABLE' ? 'primary' : 'default'}
          onClick={() => setStatusFilter(statusFilter === 'AVAILABLE' ? null : 'AVAILABLE')}
          sx={{ fontWeight: 'bold', typography: 'cardName', cursor: 'pointer' }}
        />
        <Chip
          label={`IN_USE: ${inUseCount}`}
          color={statusFilter === 'IN_USE' ? 'primary' : 'default'}
          onClick={() => setStatusFilter(statusFilter === 'IN_USE' ? null : 'IN_USE')}
          sx={{ fontWeight: 'bold', typography: 'cardName', cursor: 'pointer' }}
        />
        <Chip
          label={`DISCONNECTED: ${disconnectedCount}`}
          color={statusFilter === 'DISCONNECTED' ? 'primary' : 'default'}
          onClick={() => setStatusFilter(statusFilter === 'DISCONNECTED' ? null : 'DISCONNECTED')}
          sx={{ fontWeight: 'bold', typography: 'cardName', cursor: 'pointer' }}
        />
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, justifyContent: 'flex-end', flex: 1 }}>
        <HubHealthStatus />
        <IconButton onClick={toggleLogFrameVisibility} color="primary">
          {showLogFrame ? <VisibilityOff /> : <Visibility />}
        </IconButton>
      </Box>
    </Box>
  );
  // Central frame content: virtualized table
  const centralFrameContent = (
    <TableContainer component={Paper} sx={{ mt: 2 }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell align="left">RASPI ID</TableCell>
            <TableCell align="left">INDICATORS</TableCell>
            <TableCell align="left">IP ADDRESS</TableCell>
            <TableCell align="left">EXPERIMENT</TableCell>
            <TableCell align="left">PERIPHERALS</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredRaspis.map(raspi => {
            // Find the most recent experiment for this raspi_id
            const experimentsForRaspi = Object.values(allExperiments).filter(exp => exp.raspi_id === raspi.raspi_id);
            let lastExperiment: Experiment | undefined = undefined;
            if (experimentsForRaspi.length > 0) {
              lastExperiment = experimentsForRaspi.reduce((latest: Experiment | undefined, exp: Experiment) => {
                if (!latest) return exp;
                // Prefer start_time if available, else use exp_id (assuming incrementing IDs)
                if (exp.start_time && latest.start_time) {
                  return exp.start_time > latest.start_time ? exp : latest;
                } else if (exp.start_time) {
                  return exp;
                } else if (latest.start_time) {
                  return latest;
                } else {
                  // Fallback: compare exp_id as numbers
                  const expNum = parseInt((exp.exp_id || '').replace('E', '')) || 0;
                  const latestNum = parseInt((latest.exp_id || '').replace('E', '')) || 0;
                  return expNum > latestNum ? exp : latest;
                }
              }, undefined);
            }
            return (
              <TableRow key={raspi.raspi_id}>
                <TableCell align="left">
                  <Box
                    component="span"
                    sx={{ color: 'primary.main', textDecoration: 'underline', cursor: 'pointer', fontWeight: 'bold' }}
                    onClick={() => navigate(`/raspi-details?raspi_id=${raspi.raspi_id}`)}
                  >
                    {raspi.raspi_id}
                  </Box>
                </TableCell>
                <TableCell align="left" sx={{ textAlign: 'left' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 1, width: '100%' }}>
                    <Box sx={{ flex: '0 1 auto' }}>
                      <RaspiStatusIndicator
                        status={raspi.raspi_status}
                        statusDescription={undefined}
                        health={raspi.raspi_health_status}
                        healthDescription={raspi.raspi_health_message}
                        alert={raspi.raspi_alert}
                        alertDescription={raspi.raspi_alert_description}
                      />
                    </Box>
                    <RaspiMenu
                      raspiId={raspi.raspi_id}
                      raspiStatus={raspi.raspi_status}
                      isViewer={isViewer}
                      triggerUnauthorized={triggerUnauthorized}
                      onLoadRaspiData={handleLoadRaspiData}
                      onCleanRaspiFiles={handleCleanRaspiFiles}
                      onRebootRaspi={handleRebootRaspi}
                      onRestartRaspiSoftware={handleRestartRaspiSoftware}
                      onUpdateRaspiSoftware={handleUpdateRaspiSoftware}
                    />
                  </Box>
                </TableCell>
                <TableCell align="left">{raspi.raspi_ip}</TableCell>
                <TableCell align="left">
                  {lastExperiment ? (
                    <Box
                      component="span"
                      sx={{ color: 'primary.main', textDecoration: 'underline', cursor: 'pointer', fontWeight: 'bold' }}
                      onClick={() => navigate(`/experiments?exp_id=${lastExperiment.exp_id}`)}
                    >
                      {lastExperiment.exp_id}
                    </Box>
                  ) : (
                    <Typography variant="fieldLabel" color="text.secondary">—</Typography>
                  )}
                </TableCell>
                <TableCell align="left">
                  {/* PERIPHERALS: CHAMBERS and CAMERAS */}
                  {(() => {
                    // Find the connected chamber(s) for this raspi — exclude stale
                    // DISCONNECTED rows (a Pi accumulates historical chamber
                    // associations in the DB; only the live one matters here).
                    const chambers = Object.values(allChambers).filter((ch: any) => ch.raspi_id === raspi.raspi_id && ch.chamber_status !== 'DISCONNECTED');
                    // Count connected cameras (not DISCONNECTED)
                    const connectedCameras = Object.values(allCameras).filter((c: any) => c.raspi_id === raspi.raspi_id && c.camera_status !== 'DISCONNECTED').length;
                    return (
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5 }}>
                        {chambers.length === 0 ? (
                          <Typography variant="fieldLabel" sx={{ fontWeight: 'bold' }}>CHAMBERS: —</Typography>
                        ) : (
                          chambers.map((ch: any) => {
                            const actualOn = ch.chamber_status === 'READINESS';
                            const hasPending = Object.prototype.hasOwnProperty.call(pendingReady, ch.chamber_id);
                            // Optimistic position: reflect the click immediately.
                            const isOn = hasPending ? pendingReady[ch.chamber_id] : actualOn;
                            const pending = hasPending && pendingReady[ch.chamber_id] !== actualOn;
                            const p = ch.chamber_pressure;
                            const t = ch.chamber_temperature;
                            const hasLive = p != null || t != null;
                            // Toggle allowed when idle or already ready; not during a
                            // real run or when disconnected. (Starting a test while
                            // ready is handled by the normal start flow and supersedes.)
                            const canToggle = ch.chamber_status !== 'DISCONNECTED' && ch.chamber_status !== 'RUN';
                            const vis = readyVisual(isOn, t);
                            return (
                              <Box key={ch.chamber_id} sx={{ display: 'flex', flexDirection: 'column', mb: 0.5 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <Typography variant="fieldLabel" sx={{ fontWeight: 'bold' }}>{ch.chamber_id}</Typography>
                                  <FormControlLabel
                                    sx={{ m: 0, ml: 0.5 }}
                                    control={
                                      <Switch
                                        size="small"
                                        color={vis.sw}
                                        checked={isOn}
                                        disabled={!canToggle || pending}
                                        onChange={(e) => handleReadyToggle(raspi.raspi_id, ch.chamber_id, e.target.checked)}
                                      />
                                    }
                                    label={
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <Box sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: vis.dot, flexShrink: 0 }} />
                                        <Typography variant="fieldLabel">Ready</Typography>
                                        {pending && <CircularProgress size={11} thickness={6} />}
                                      </Box>
                                    }
                                  />
                                </Box>
                                <Typography variant="fieldLabel" sx={{ color: isOn ? vis.txt : (hasLive ? 'text.primary' : 'text.secondary'), fontWeight: isOn ? 600 : 400 }}>
                                  {`${p != null ? Number(p).toFixed(1) : '—'} mmHg · ${t != null ? Number(t).toFixed(1) : '—'} °C`}
                                </Typography>
                              </Box>
                            );
                          })
                        )}
                        <Typography variant="fieldLabel" sx={{ fontWeight: 'bold' }}>
                          CAMERAS: {connectedCameras}
                        </Typography>
                      </Box>
                    );
                  })()}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );

  // Log frame content with expand/collapse arrow - use useMemo to make it reactive
  const logFrameContent = useMemo(() => (
    <Box sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexShrink: 0 }}>
        <IconButton 
          onClick={toggleLogFrameExpansion}
          size="small"
          sx={{ p: 0.5 }}
        >
          {isLogFrameExpanded ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
        </IconButton>
        <Typography variant="h6" gutterBottom>
          SYSTEM LOGS
        </Typography>
        <Box 
          sx={{ 
            width: 8, 
            height: 8, 
            borderRadius: '50%', 
            bgcolor: '#4caf50',
            ml: 1
          }} 
        />
        <Typography variant="caption" color="text.secondary">
          Live
        </Typography>
      </Box>
      
      {/* Log Content - always show full logs */}
      <Box sx={{ 
        flexGrow: 1, 
        overflow: 'auto',
        height: 0, // This ensures the box takes remaining space
        border: '1px solid #e0e0e0',
        borderRadius: '4px',
        backgroundColor: '#fafafa'
      }}>
        {logLoading ? (
          <Box display="flex" justifyContent="center" p={2}>
            <CircularProgress size={24} />
          </Box>
        ) : logError ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {logError}
          </Alert>
        ) : logEntries.length > 0 ? (
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            {logEntries.map((entry, index) => (
              <Box 
                key={index} 
                sx={{ 
                  p: 1, 
                  borderBottom: '1px solid #f0f0f0',
                  '&:last-child': { borderBottom: 'none' },
                  '&:hover': { backgroundColor: '#f5f5f5' }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <Typography 
                    variant="logSection" 
                    sx={{ 
                      color: getLogLevelColor(entry.level),
                      minWidth: '60px',
                      flexShrink: 0
                    }}
                  >
                    {entry.level}
                  </Typography>
                  <Typography 
                    variant="logSection" 
                    sx={{ 
                      color: getSourceColor(entry.source_component),
                      minWidth: '80px',
                      flexShrink: 0
                    }}
                  >
                    {entry.source_component}
                  </Typography>
                  <Typography 
                    variant="caption" 
                    color="text.secondary"
                    sx={{ 
                      typography: 'logText',
                      minWidth: '80px',
                      flexShrink: 0
                    }}
                  >
                    {entry.timestamp}
                  </Typography>
                </Box>
                <Typography 
                  variant="logText"
                  sx={{ 
                    wordBreak: 'break-word',
                    ml: 17,
                    mt: 0.5
                  }}
                >
                  {entry.message}
                </Typography>
              </Box>
            ))}
          </Box>
        ) : (
          <Typography color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
            No log entries found
          </Typography>
        )}
      </Box>
    </Box>
  ), [isLogFrameExpanded, logLoading, logError, logEntries, toggleLogFrameExpansion]);

  return (
    <>
      <PageLayout
        upFrameContent={upFrameContent}
        centralFrameContent={centralFrameContent}
        logFrameContent={logFrameContent}
        showLogFrame={showLogFrame}
        isLogFrameExpanded={isLogFrameExpanded}
      />
      {/* Dialogs for menu actions */}
      <LoadRaspiDataDialog
        open={loadRaspiDataDialogOpen}
        onClose={() => setLoadRaspiDataDialogOpen(false)}
        raspiId={activeRaspiId || ''}
      />
      <CleanRaspiFilesDialog
        open={cleanRaspiFilesDialogOpen}
        onClose={() => setCleanRaspiFilesDialogOpen(false)}
        raspiId={activeRaspiId || ''}
      />
      <UpdateRaspiSoftwareDialog
        open={updateRaspiSoftwareDialogOpen}
        onClose={() => setUpdateRaspiSoftwareDialogOpen(false)}
        raspiId={activeRaspiId || ''}
        currentRaspiVersion={(() => {
          const raspi = allRaspis[activeRaspiId || ''];
          return raspi?.raspi_software_version || '1.0.0';
        })()}
      />
      <Dialog open={rebootRaspiDialogOpen} onClose={() => setRebootRaspiDialogOpen(false)}>
        <DialogTitle>Warning: Reboot RASPI</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
              Rebooting the RASPI is a dangerous operation.
            </Typography>
            <Typography variant="body2">
              This will interrupt all processes and may cause data loss or system instability.
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              Are you sure you want to continue?
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRebootRaspiDialogOpen(false)} color="inherit">Cancel</Button>
          <Button onClick={handleConfirmReboot} color="error" variant="contained">Reboot Anyway</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={restartRaspiDialogOpen} onClose={() => setRestartRaspiDialogOpen(false)}>
        <DialogTitle>Warning: Restart RASPI Software</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
              Restarting the software will interrupt all processes and may cause data loss or system instability.
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              Are you sure you want to continue?
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRestartRaspiDialogOpen(false)} color="inherit">Cancel</Button>
          <Button onClick={handleConfirmRestart} color="error" variant="contained">Restart Anyway</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Dashboard;
